/**
 * Accordion Search Functionality
 * Provides search and highlighting for accordion blocks
 */
(function() {
  'use strict';

  /**
   * Initializes accordion search for a given accordion container
   * @param {string} accordionSelector - CSS selector for the accordion container
   */
  function initAccordionSearch(accordionSelector) {
    const accordion = document.querySelector(accordionSelector);
    if (!accordion) return;

    const searchInput = accordion.querySelector('.accordion__search-input');
    const clearButton = accordion.querySelector('.accordion__search-clear');
    const accordionItems = accordion.querySelectorAll('.accordion__items accordion-custom');

    if (!searchInput || !clearButton || !accordionItems.length) return;

    /**
     * Gets searchable text from an accordion item
     * @param {HTMLElement} accordionItem - The accordion item element
     * @returns {string} Lowercase searchable text
     */
    function getSearchableText(accordionItem) {
      const summary = accordionItem.querySelector('summary');
      const content = accordionItem.querySelector('.details-content');

      let text = '';
      if (summary) {
        // Get text from summary (heading/question) excluding icons
        const summaryClone = /** @type {HTMLElement} */ (summary.cloneNode(true));
        const icons = summaryClone.querySelectorAll('.svg-wrapper, .details__icon');
        icons.forEach(/** @type {HTMLElement} */ (icon) => icon.remove());
        const summaryText = summaryClone.textContent || '';
        text += summaryText.trim() + ' ';
      }
      if (content) {
        // Get text from content (answer)
        const contentText = content.textContent || '';
        text += contentText.trim();
      }
      return text.toLowerCase();
    }

    /**
     * Removes all highlight marks from an element
     * @param {HTMLElement} element - The element to remove highlights from
     */
    function removeHighlights(element) {
      const highlights = element.querySelectorAll('.accordion__search-highlight');
      highlights.forEach(/** @type {HTMLElement} */ (highlight) => {
        const parent = highlight.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(highlight.textContent), highlight);
          parent.normalize();
        }
      });
    }

    /**
     * Highlights matching text in an element
     * @param {HTMLElement} element - The element to highlight text in
     * @param {string} searchTerm - The search term to highlight
     */
    function highlightText(element, searchTerm) {
      if (!searchTerm || searchTerm.trim() === '') {
        removeHighlights(element);
        return;
      }

      const term = searchTerm.trim();
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: function(node) {
            // Skip text nodes inside icons, SVG wrappers, or existing highlights
            let parent = node.parentElement;
            while (parent && parent !== element) {
              if (parent.classList.contains('svg-wrapper') ||
                  parent.classList.contains('details__icon') ||
                  parent.classList.contains('accordion__search-highlight')) {
                return NodeFilter.FILTER_REJECT;
              }
              parent = parent.parentElement;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const textNodes = [];
      let node;
      while (node = walker.nextNode()) {
        textNodes.push(node);
      }

      textNodes.forEach(textNode => {
        const text = textNode.textContent;
        if (!text) return;

        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedTerm})`, 'gi');

        if (regex.test(text)) {
          const highlightedText = text.replace(regex, '<mark class="accordion__search-highlight">$1</mark>');
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = highlightedText;

          const parent = textNode.parentNode;
          if (!parent) return;

          const fragment = document.createDocumentFragment();

          // Move all nodes from tempDiv to fragment
          while (tempDiv.firstChild) {
            fragment.appendChild(tempDiv.firstChild);
          }

          // Replace text node with fragment
          parent.replaceChild(fragment, textNode);
        }
      });
    }

    /**
     * Updates visibility of group headings based on whether their items are visible
     */
    function updateGroupHeadingVisibility() {
      if (!accordion) return;
      const itemsContainer = accordion.querySelector('.accordion__items');
      if (!itemsContainer) return;

      const children = Array.from(itemsContainer.children);
      /** @type {Element | null} */
      let currentHeading = null;
      let hasVisibleItems = false;

      for (let i = 0; i < children.length; i++) {
        const child = /** @type {Element} */ (children[i]);

        if (child.hasAttribute('data-accordion-group-heading')) {
          if (currentHeading) {
            if (hasVisibleItems) {
              currentHeading.removeAttribute('data-hidden');
            } else {
              currentHeading.setAttribute('data-hidden', 'true');
            }
          }
          currentHeading = child;
          hasVisibleItems = false;
        } else if (child.tagName.toLowerCase() === 'accordion-custom') {
          if (child.getAttribute('data-hidden') !== 'true') {
            hasVisibleItems = true;
          }
        }
      }

      if (currentHeading) {
        if (hasVisibleItems) {
          currentHeading.removeAttribute('data-hidden');
        } else {
          currentHeading.setAttribute('data-hidden', 'true');
        }
      }
    }

    /**
     * Filters accordion items based on search term
     * @param {string} searchTerm - The search term to filter by
     * @returns {boolean} Whether any results were found
     */
    function filterAccordionItems(searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      const minSearchLength = 3;
      let hasResults = false;

      // Only filter if search is empty or has at least 3 characters
      const shouldFilter = term === '' || term.length >= minSearchLength;

      accordionItems.forEach(item => {
        const summaryEl = item.querySelector('summary');
        const contentEl = item.querySelector('.details-content');
        const summary = summaryEl ? /** @type {HTMLElement} */ (summaryEl) : null;
        const content = contentEl ? /** @type {HTMLElement} */ (contentEl) : null;
        const details = item.querySelector('details');

        // Remove existing highlights first
        if (summary) removeHighlights(summary);
        if (content) removeHighlights(content);

        if (!shouldFilter) {
          // Show all items without filtering when search is less than 3 characters
          item.removeAttribute('data-hidden');
          // Don't change accordion state when search is too short - keep them as they are
          hasResults = true;
          return;
        }

        const searchableText = getSearchableText(/** @type {HTMLElement} */ (item));
        const matches = term === '' || searchableText.includes(term);

        if (matches) {
          item.removeAttribute('data-hidden');

          // Add highlights if there's a search term
          if (term !== '') {
            if (summary) highlightText(summary, searchTerm);
            if (content) highlightText(content, searchTerm);

            // Automatically open matching accordion items
            if (details && !details.open) {
              details.open = true;
            }
          } else {
            // Close all accordions when search is cleared
            if (details && details.open) {
              details.open = false;
            }
          }

          hasResults = true;
        } else {
          item.setAttribute('data-hidden', 'true');
        }
      });

      updateGroupHeadingVisibility();

      return hasResults;
    }

    function updateClearButton() {
      const input = /** @type {HTMLInputElement} */ (searchInput);
      if (input && input.value.trim() !== '') {
        if (clearButton) {
          clearButton.classList.remove('visually-hidden');
        }
      } else if (clearButton) {
        clearButton.classList.add('visually-hidden');
      }
    }

    if (searchInput) {
      const input = /** @type {HTMLInputElement} */ (searchInput);
      input.addEventListener('input', (e) => {
        const target = /** @type {HTMLInputElement} */ (e.target);
        filterAccordionItems(target.value);
        updateClearButton();
      });
    }

    if (clearButton) {
      clearButton.addEventListener('click', () => {
        const input = /** @type {HTMLInputElement} */ (searchInput);
        if (input) {
          input.value = '';
          filterAccordionItems('');
          updateClearButton();
          input.focus();
        }
      });
    }

    // Initialize clear button visibility
    updateClearButton();
  }

  // Export for use in Liquid templates
  // @ts-ignore
  window.initAccordionSearch = initAccordionSearch;
})();
